import React, { useState, useEffect, useRef } from "react";
import QRCode from "qrcode";
import {
  initiateAuth,
  respondToNewPassword,
  associateSoftwareToken,
  verifySoftwareToken,
  respondToMfaChallenge,
  getTotpUri,
} from "./auth";
import "./Login.css";

/**
 * Screens in order:
 *   login          → username + password
 *   new_password   → forced on first login
 *   mfa_setup      → shown once ever: scan QR code + verify
 *   mfa            → every login: enter 6-digit TOTP
 */

export default function LoginScreen({ onAuthenticated }) {
  const [screen,      setScreen]      = useState("login");
  const [username,    setUsername]    = useState("");
  const [password,    setPassword]    = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [totpCode,    setTotpCode]    = useState("");
  const [session,     setSession]     = useState(null);
  const [secretCode,  setSecretCode]  = useState(null);
  const [qrUrl,       setQrUrl]       = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);

  const totpRef = useRef(null);

  useEffect(() => {
    if ((screen === "mfa" || screen === "mfa_verify") && totpRef.current) {
      totpRef.current.focus();
    }
  }, [screen]);

  // Generate the QR code image entirely client-side. Previously this sent
  // the TOTP secret to Google's (deprecated) Charts API in a URL query
  // string to render the image — that leaked the MFA seed to a third party
  // over the network. The `qrcode` package renders directly to a data: URL,
  // so the secret never leaves the browser.
  useEffect(() => {
    if (secretCode && username) {
      const uri = getTotpUri(username, secretCode);
      QRCode.toDataURL(uri, { width: 200, margin: 1 })
        .then(setQrUrl)
        .catch(err => {
          console.error("QR code generation failed:", err);
          setQrUrl(null);
        });
    }
  }, [secretCode, username]);

  function setErr(e) {
    const msg = e?.message || String(e);
    // Map Cognito error codes to human-readable messages
    if (msg.includes("NotAuthorizedException") || msg.includes("Incorrect username or password"))
      return setError("Incorrect username or password.");
    if (msg.includes("UserNotFoundException"))
      return setError("User not found.");
    if (msg.includes("TooManyRequestsException"))
      return setError("Too many attempts. Please wait a moment.");
    if (msg.includes("CodeMismatchException") || msg.includes("TOTP verification failed"))
      return setError("Invalid code — check your authenticator app and try again.");
    if (msg.includes("ExpiredCodeException"))
      return setError("Code expired — please generate a new one.");
    if (msg.includes("InvalidPasswordException"))
      return setError("Password does not meet requirements (14+ chars, upper, lower, number, symbol).");
    setError(msg);
  }

  // ── Step 1: Login ──────────────────────────────────────────────────────────
  async function handleLogin(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await initiateAuth(username, password);
      if (result.challenge === "NEW_PASSWORD_REQUIRED") {
        setSession(result.session);
        setScreen("new_password");
      } else if (result.challenge === "SOFTWARE_TOKEN_MFA") {
        setSession(result.session);
        setScreen("mfa");
      } else if (result.challenge === "MFA_SETUP") {
        setSession(result.session);
        await beginMfaSetup(result.session);
      } else if (!result.challenge) {
        onAuthenticated();
      }
    } catch (err) {
      setErr(err);
    } finally {
      setLoading(false);
    }
  }

  // ── Step 2: New password ───────────────────────────────────────────────────
  async function handleNewPassword(e) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPass) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const result = await respondToNewPassword(username, newPassword, session);
      if (result.challenge === "MFA_SETUP") {
        setSession(result.session);
        await beginMfaSetup(result.session);
      } else if (result.challenge === "SOFTWARE_TOKEN_MFA") {
        setSession(result.session);
        setScreen("mfa");
      } else if (!result.challenge) {
        onAuthenticated();
      }
    } catch (err) {
      setErr(err);
    } finally {
      setLoading(false);
    }
  }

  // ── Step 3a: Begin MFA setup ───────────────────────────────────────────────
  async function beginMfaSetup(sess) {
    setLoading(true);
    try {
      const { secretCode: sc, session: newSess } = await associateSoftwareToken(sess);
      setSecretCode(sc);
      setSession(newSess);
      setScreen("mfa_setup");
    } catch (err) {
      setErr(err);
    } finally {
      setLoading(false);
    }
  }

  // ── Step 3b: Verify MFA setup ──────────────────────────────────────────────
  async function handleMfaSetupVerify(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await verifySoftwareToken(session, totpCode);
      if (result.complete) {
        // SRP flow: the library answers the MFA_SETUP challenge itself —
        // enrollment completes the sign-in, no second TOTP prompt.
        onAuthenticated();
      } else {
        setSession(result.session);
        setTotpCode("");
        setScreen("mfa");
      }
    } catch (err) {
      setErr(err);
    } finally {
      setLoading(false);
    }
  }

  // ── Step 4: MFA challenge (every login) ────────────────────────────────────
  async function handleMfaChallenge(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await respondToMfaChallenge(username, totpCode, session);
      onAuthenticated();
    } catch (err) {
      setTotpCode("");
      setErr(err);
    } finally {
      setLoading(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="login-root">
      <div className="login-card">
        <div className="login-logo">
          <span className="login-icon">🏠</span>
          <h1 className="login-title">Cocoon</h1>
          <p className="login-sub">Home Maintenance Tracker</p>
        </div>

        {error && (
          <div className="login-error" role="alert">
            <span>⚠</span> {error}
          </div>
        )}

        {/* ── Login form ── */}
        {screen === "login" && (
          <form className="login-form" onSubmit={handleLogin}>
            <label>
              Username
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? <span className="spinner" /> : "Sign In"}
            </button>
          </form>
        )}

        {/* ── New password (first login) ── */}
        {screen === "new_password" && (
          <form className="login-form" onSubmit={handleNewPassword}>
            <div className="login-notice">
              <strong>Set your password</strong>
              <p>This is your first login. Choose a permanent password.</p>
            </div>
            <label>
              New Password
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                autoComplete="new-password"
                autoFocus
                required
                minLength={14}
                placeholder="14+ chars, upper, lower, number, symbol"
              />
            </label>
            <label>
              Confirm Password
              <input
                type="password"
                value={confirmPass}
                onChange={e => setConfirmPass(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? <span className="spinner" /> : "Set Password"}
            </button>
          </form>
        )}

        {/* ── MFA Setup (one-time) ── */}
        {screen === "mfa_setup" && (
          <form className="login-form" onSubmit={handleMfaSetupVerify}>
            <div className="login-notice">
              <strong>Set up two-factor authentication</strong>
              <p>Scan the QR code with Google Authenticator, Authy, or any TOTP app.</p>
            </div>

            {qrUrl && (
              <div className="qr-container">
                <img src={qrUrl} alt="TOTP QR Code" width={180} height={180} />
              </div>
            )}

            {secretCode && (
              <div className="secret-code">
                <span className="secret-label">Manual entry key</span>
                <code className="secret-value">{secretCode}</code>
              </div>
            )}

            <label>
              Enter the 6-digit code from your app to confirm setup
              <input
                ref={totpRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                autoComplete="one-time-code"
                required
              />
            </label>
            <button type="submit" className="login-btn" disabled={loading || totpCode.length !== 6}>
              {loading ? <span className="spinner" /> : "Verify & Activate"}
            </button>
          </form>
        )}

        {/* ── MFA challenge (every login) ── */}
        {screen === "mfa" && (
          <form className="login-form" onSubmit={handleMfaChallenge}>
            <div className="login-notice mfa-notice">
              <span className="mfa-icon">🔐</span>
              <div>
                <strong>Two-factor authentication</strong>
                <p>Enter the 6-digit code from your authenticator app.</p>
              </div>
            </div>
            <label>
              Authenticator Code
              <input
                ref={totpRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                autoComplete="one-time-code"
                required
              />
            </label>
            <button type="submit" className="login-btn" disabled={loading || totpCode.length !== 6}>
              {loading ? <span className="spinner" /> : "Verify"}
            </button>
            <button
              type="button"
              className="login-back"
              onClick={() => { setScreen("login"); setTotpCode(""); setError(null); }}
            >
              ← Back to login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
